import React from 'react';

import { History, InsertDriveFileOutlined } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';

import { ReceivedFile } from './FileReceiver';

export interface RecentFile {
  id: string; // unique id for IndexedDB
  fileName: string;
  filePath: string;
  openedAt: number;
  parserMetadata?: {
    parser?: string;
    app?: string;
  };
}

const RECENT_FILES_STORAGE_KEY = 'boltMobileLogViewerRecentFiles';
const INDEXED_DB_NAME = 'boltMobileLogViewerDB';
const INDEXED_DB_STORE = 'fileHandles';
const MAX_RECENT_FILES = 5;

// Check if File System Access API is supported
export function isFileSystemAccessSupported(): boolean {
  return 'showOpenFilePicker' in window;
}

// IndexedDB helpers for storing FileSystemFileHandle
class FileHandleStorage {
  private db: IDBDatabase | null = null;

  async open(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(INDEXED_DB_NAME, 1);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(INDEXED_DB_STORE)) {
          db.createObjectStore(INDEXED_DB_STORE, { keyPath: 'id' });
        }
      };
    });
  }

  async saveHandle(id: string, handle: FileSystemFileHandle): Promise<void> {
    if (!this.db) {
      await this.open();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(INDEXED_DB_STORE, 'readwrite');
      const store = transaction.objectStore(INDEXED_DB_STORE);
      const request = store.put({ id, handle });

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  async getHandle(id: string): Promise<FileSystemFileHandle | null> {
    if (!this.db) {
      await this.open();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(INDEXED_DB_STORE, 'readonly');
      const store = transaction.objectStore(INDEXED_DB_STORE);
      const request = store.get(id);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        resolve(request.result?.handle || null);
      };
    });
  }

  async deleteHandle(id: string): Promise<void> {
    if (!this.db) {
      await this.open();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(INDEXED_DB_STORE, 'readwrite');
      const store = transaction.objectStore(INDEXED_DB_STORE);
      const request = store.delete(id);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  async clear(): Promise<void> {
    if (!this.db) {
      await this.open();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(INDEXED_DB_STORE, 'readwrite');
      const store = transaction.objectStore(INDEXED_DB_STORE);
      const request = store.clear();

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }
}

export class RecentFilesManager {
  private recentFiles: RecentFile[] = [];
  private handleStorage = new FileHandleStorage();
  onChange?: () => void;

  async init(): Promise<void> {
    await this.handleStorage.open();
    this.load();
  }

  load(): void {
    const value = localStorage.getItem(RECENT_FILES_STORAGE_KEY);
    if (!value) {
      return;
    }

    try {
      this.recentFiles = JSON.parse(value);
    } catch (error) {
      console.error('Error parsing recent files', error);
      this.recentFiles = [];
    }
  }

  save(): void {
    localStorage.setItem(RECENT_FILES_STORAGE_KEY, JSON.stringify(this.recentFiles));
    this.onChange?.();
  }

  async addFile(
    fileName: string,
    filePath: string,
    handle: FileSystemFileHandle | null,
    parserMetadata?: { parser?: string; app?: string },
  ): Promise<void> {
    const id = crypto.randomUUID();

    // Remove existing entry with same filename if exists
    for (const existing of this.recentFiles.filter((f) => f.fileName === fileName)) {
      await this.handleStorage.deleteHandle(existing.id);
    }
    this.recentFiles = this.recentFiles.filter((f) => f.fileName !== fileName);

    // Add new entry at the beginning
    this.recentFiles.unshift({
      id,
      fileName,
      filePath,
      openedAt: Date.now(),
      parserMetadata,
    });

    // Store handle in IndexedDB
    if (handle) {
      await this.handleStorage.saveHandle(id, handle);
    }

    // Keep only the most recent files
    while (this.recentFiles.length > MAX_RECENT_FILES) {
      const removed = this.recentFiles.pop();
      if (removed) {
        await this.handleStorage.deleteHandle(removed.id);
      }
    }

    this.save();
  }

  getRecentFiles(): RecentFile[] {
    return this.recentFiles;
  }

  async getFileHandle(id: string): Promise<FileSystemFileHandle | null> {
    return this.handleStorage.getHandle(id);
  }

  async clear(): Promise<void> {
    await this.handleStorage.clear();
    this.recentFiles = [];
    this.save();
  }
}

interface RecentFilesListProps {
  recentFilesManager: RecentFilesManager;
  onFileSelected: (files: ReceivedFile[]) => void;
}

interface RecentFilesListState {
  loadingFileId: string | null;
}

export class RecentFilesList extends React.Component<RecentFilesListProps, RecentFilesListState> {
  constructor(props: RecentFilesListProps) {
    super(props);
    this.state = {
      loadingFileId: null,
    };
  }

  componentDidMount() {
    this.props.recentFilesManager.onChange = () => {
      this.forceUpdate();
    };
  }

  render() {
    const recentFiles = this.props.recentFilesManager.getRecentFiles();

    if (recentFiles.length === 0) {
      return null;
    }

    return (
      <Box className="recent-files-container">
        <Box className="recent-files-header">
          <History className="recent-files-header-icon" />
          <Typography variant="h6" className="recent-files-title">
            Recent
          </Typography>
          <Box flex={1} />
          <Button
            size="small"
            className="recent-files-clear-button"
            onClick={() => this.props.recentFilesManager.clear()}
          >
            Clear
          </Button>
        </Box>

        <List className="recent-files-list" disablePadding>
          {recentFiles.map((file) => (
            <ListItemButton
              key={file.id}
              className="recent-file-item"
              onClick={() => this.handleFileClick(file)}
              disabled={this.state.loadingFileId === file.id}
            >
              <ListItemIcon className="recent-file-icon">
                {this.state.loadingFileId === file.id ? (
                  <CircularProgress size={16} />
                ) : (
                  <InsertDriveFileOutlined />
                )}
              </ListItemIcon>
              <ListItemText
                primary={<Typography className="recent-file-name">{file.fileName}</Typography>}
                secondary={
                  <Typography className="recent-file-path">{this.getDisplayPath(file)}</Typography>
                }
              />
              <Typography className="recent-file-date">{this.formatDate(file.openedAt)}</Typography>
            </ListItemButton>
          ))}
        </List>

        {!isFileSystemAccessSupported() && (
          <Typography className="recent-files-warning">
            Use Chrome/Edge for click-to-open
          </Typography>
        )}
      </Box>
    );
  }

  private async handleFileClick(file: RecentFile): Promise<void> {
    if (!isFileSystemAccessSupported()) {
      return;
    }

    this.setState({ loadingFileId: file.id });

    try {
      const handle = await this.props.recentFilesManager.getFileHandle(file.id);
      if (!handle) {
        console.error('File handle not found');
        this.setState({ loadingFileId: null });
        return;
      }

      // Request permission to read the file
      const permission = await handle.requestPermission({ mode: 'read' });
      if (permission !== 'granted') {
        console.error('Permission denied');
        this.setState({ loadingFileId: null });
        return;
      }

      // Read the file
      const fileData = await handle.getFile();
      const contents = await fileData.arrayBuffer();

      const receivedFile: ReceivedFile = {
        fileName: file.fileName,
        contents,
        handle, // Pass handle so it's preserved when added to recent files again
      };

      this.props.onFileSelected([receivedFile]);
    } catch (error) {
      console.error('Error opening file:', error);
      alert('Could not open file. It may have been moved or deleted.');
    } finally {
      this.setState({ loadingFileId: null });
    }
  }

  private getDisplayPath(file: RecentFile): string {
    const metaParts: string[] = [];
    if (file.parserMetadata?.app) {
      metaParts.push(file.parserMetadata.app);
    }

    if (metaParts.length > 0) {
      return metaParts.join(' • ');
    }

    return file.filePath;
  }

  private formatDate(timestamp: number): string {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    if (diffDays === 1) {
      return 'Yesterday';
    }
    if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  }
}
